<template>
  <div class="d-flex justify-content-between">
    <div class="d-flex align-items-center mb-3">
      <span class="me-2">Group Name: </span>
      <span>{{ emailGroup.name }}</span>
    </div>
    <div class="d-flex align-items-center mb-3">
      <span class="me-2">Last Updated: </span>
      <span>{{ formatDateTime(emailGroup.updatedAt) }}</span>
    </div>
  </div>
  <div class="email-group-members">
    <div class="d-flex">
      <table class="table mb-0">
        <thead>
          <tr>
            <th scope="col">
              Email
            </th>
            <th scope="col" class="email-group-member-last-updated-column">
              Last Updated
            </th>
            <th scope="col" class="email-group-members-actions-column">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="initialRequestInProgress">
            <td colspan="4" class="align-middle">
              <div class="d-flex justify-content-center align-items-center">
                <span>Loading</span><i class="bx bx-loader-alt bx-spin ms-2" />
              </div>
            </td>
          </tr>
          <tr
            v-for="member in emailGroupMembers"
            v-else-if="emailGroupMembers.length"
            :key="member.email"
          >
            <td class="align-middle">
              <email-group-member-email-cell
                :company="company"
                :email-group="emailGroup"
                :email-group-member="member"
                @email-group-member-updated="onEmailGroupMemberUpdated"
              />
            </td>
            <td class="align-middle email-group-member-last-updated-column">{{ formatDateTime(member.updatedAt) }}</td>
            <td class="align-middle email-group-members-actions-column">
              <GuButton
                :disabled="deleteRequestInProgress"
                variant="danger"
                size="sm"
                @click="onDeleteEmailGroupMember(member)"
              >
                Delete
              </GuButton>
            </td>
          </tr>
          <tr v-else>
            <td colspan="4" class="align-middle">
              <div class="d-flex justify-content-center align-items-center">
                No members in email group
              </div>
            </td>
          </tr>
          <tr>
            <td colspan="3" class="border-bottom-0 pt-4 pb-0 d-flex">
              <template v-if="addNewEmailToGroup">
                <GuInput
                  v-model="newMemberEmail"
                  size="sm"
                  @keyup.enter="saveNewEmailGroupMember"
                />
                <GuButton
                  :disabled="!newMemberEmail || requestInProgress"
                  variant="primary"
                  size="sm"
                  class="ms-2"
                  aria-label="Save Email Group"
                  @click="saveNewEmailGroupMember"
                >
                  Save
                </GuButton>
                <GuButton
                  variant="secondary"
                  size="sm"
                  class="ms-2"
                  aria-label="Cancel"
                  @click="addNewEmailToGroup = false"
                >
                  Cancel
                </GuButton>
              </template>
              <template v-else>
                <GuButton
                  :disabled="requestInProgress"
                  variant="primary"
                  size="sm"
                  aria-label="Add Email To Group"
                  @click="showNewEmailInput"
                >
                  Add Email
                </GuButton>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, ref, defineProps } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { GuInput } from '@shared/gu-components/input'

import { 
    createCompanyEmailGroupMember,
    deleteCompanyEmailGroupMember,
    getCompanyEmailGroupMembers,
} from '@companies/api/companies.api'
import { useCompanyStore } from '@companies/stores/CompanyStore'

import { useToast } from '@shared/gu-components/toast'

import { formatDate } from '@shared/utils/date'
import { DATE_FORMAT_LONG } from '@templates/constants/dateFormat'

import EmailGroupMemberEmailCell from '@companies/components/EmailGroupMemberEmailCell.vue'

const companyStore = useCompanyStore()
const { company, getCompanyId } = storeToRefs(companyStore)

const newMemberEmail = ref('')
const createRequestInProgress = ref(false)
const deleteRequestInProgress = ref(false)
const { toast } = useToast()

const props = defineProps({
  emailGroup: {
    type: Object,
    required: true,
  },
})

const {
  data: emailGroupMembersData,
  isPending: initialRequestInProgress,
} = getCompanyEmailGroupMembers(getCompanyId, props.emailGroup.id, { gcTime: 10000 })

const emailGroupMembers = computed(() => {
  return emailGroupMembersData.value ?? []
})

const addNewEmailToGroup = ref(false)

function showNewEmailInput () {
    addNewEmailToGroup.value = true
}

function formatDateTime (datetime) {
    return formatDate(datetime, DATE_FORMAT_LONG)
}

function onEmailGroupMemberUpdated (member) {
  const idx = emailGroupMembersData.value.findIndex(emailGroupMember => emailGroupMember.id === member.id) 
  emailGroupMembersData.value = emailGroupMembersData.value.with(idx, member)
}

const { mutateAsync: createCompanyEmailGroupMemberMutation } = createCompanyEmailGroupMember()
async function saveNewEmailGroupMember () {
  if (newMemberEmail.value) {
    let response
    try {
      createRequestInProgress.value = true
       response = await createCompanyEmailGroupMemberMutation({ 
        companyId: getCompanyId.value,
        companyEmailGroupId: props.emailGroup.id,
        email: newMemberEmail.value,
      })
    } catch (error) {
      createRequestInProgress.value = false
      console.error('Error occured adding email to group:', error)
      toast({
        variant: 'danger',
        description: error?.response?.data?._message || 'Error adding email to group, please try again',
        duration: Infinity,
      })
      return
    }
    createRequestInProgress.value = false
    emailGroupMembersData.value.push({
      id: response.id,
      email: response.email,
      updatedAt: response.updatedAt,
    })
    newMemberEmail.value = ''
    showNewEmailInput.value = false
    toast({
      variant: 'success',
      description: `Email "${response.email}" added successfully`,
    })
  }
}

const { mutateAsync: deleteCompanyEmailGroupMemberMutation } = deleteCompanyEmailGroupMember()
async function onDeleteEmailGroupMember (emailGroupMember) {
  deleteRequestInProgress.value = true
  let response
  try {
    response = await deleteCompanyEmailGroupMemberMutation({
      companyId: getCompanyId,
      companyEmailGroupId: props.emailGroup.id,
      companyEmailGroupMemberId: emailGroupMember.id,
    })
  } catch (error) {
    console.error('Error occured removing email member from group:', error)
    toast({
      variant: 'danger',
      description: error?.response?.data?._message || 'Error removing email from group, please try again',
    })
  }
  setTimeout(() => toast({
    variant: 'success',
    description: `Email "${emailGroupMember.email}" removed from group successfully`,
  }),0)
  if (response?.deleted) {
    emailGroupMembersData.value = emailGroupMembersData.value.filter(emailGroupMemberItem => emailGroupMemberItem.id !== emailGroupMember.id)
  }

  deleteRequestInProgress.value = false
}

</script>

<style scoped>
.email-group-members td {
  height: 48px;
}
.email-group-members-actions-column {
  width: 142px;
  max-width: 142px;
}
.email-group-member-last-updated-column {
  width: 175px;
  max-width: 175px;
}
</style>